// Pure credit cost map and credit-check logic — no supabase dependency.
// Extracted from useCredits.ts so tests and aiAction.ts can import without
// pulling in the supabase client (which requires Vite's import.meta.env).

export interface CreditCheckResult {
  allowed: boolean;
  reason: string;
  remaining_lifetime: number;
  remaining_month: number;
  remaining_day: number;
  tier: string;
  credits_needed: number;
}

export const CREDIT_COSTS: Record<string, number> = {
  "analyze-food": 2,
  "medication-scan": 2,
  "generate-recipe": 1,
  "health-score": 1,
  "check-allergens": 1,
  "nutrition-estimate": 1,
  "medication-advisor": 1,
  "medication-chat": 1,
  "medication-lookup": 1,
};

export function getCreditsForEndpoint(endpoint: string): number | null {
  return CREDIT_COSTS[endpoint] ?? null;
}

// Pure credit check — accepts the current credit status as input.
// The supabase-backed version in useCredits.ts delegates to this.
export function checkCreditsForAction(
  creditsNeeded: number | null,
  status: { tier: string; remaining_lifetime: number; remaining_month: number; remaining_day: number } | null,
): CreditCheckResult {
  if (creditsNeeded === null || creditsNeeded <= 0) {
    return {
      allowed: false,
      reason: "unknown_endpoint",
      remaining_lifetime: 0,
      remaining_month: 0,
      remaining_day: 0,
      tier: "free",
      credits_needed: 0,
    };
  }
  if (!status) {
    return {
      allowed: false,
      reason: "unavailable",
      remaining_lifetime: 0,
      remaining_month: 0,
      remaining_day: 0,
      tier: "free",
      credits_needed: creditsNeeded,
    };
  }
  const tier = status.tier ?? "free";
  const remainingLife = status.remaining_lifetime ?? 0;
  const remainingMonth = status.remaining_month ?? 0;
  const remainingDay = status.remaining_day ?? 0;

  if (tier === "free") {
    if (remainingLife >= creditsNeeded) {
      return { allowed: true, reason: "ok", remaining_lifetime: remainingLife, remaining_month: remainingMonth, remaining_day: remainingDay, tier, credits_needed: creditsNeeded };
    }
    return { allowed: false, reason: "no_lifetime_credits", remaining_lifetime: remainingLife, remaining_month: remainingMonth, remaining_day: remainingDay, tier, credits_needed: creditsNeeded };
  }

  if (remainingMonth >= creditsNeeded && remainingDay >= creditsNeeded) {
    return { allowed: true, reason: "ok", remaining_lifetime: remainingLife, remaining_month: remainingMonth, remaining_day: remainingDay, tier, credits_needed: creditsNeeded };
  }
  if (remainingDay < creditsNeeded) {
    return { allowed: false, reason: "daily_limit_reached", remaining_lifetime: remainingLife, remaining_month: remainingMonth, remaining_day: remainingDay, tier, credits_needed: creditsNeeded };
  }
  return { allowed: false, reason: "monthly_limit_reached", remaining_lifetime: remainingLife, remaining_month: remainingMonth, remaining_day: remainingDay, tier, credits_needed: creditsNeeded };
}
